import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import es from './locales/es.json';

// Solo el español va dentro del JavaScript principal. El resto de idiomas se
// descarga aparte cuando hace falta: antes iban los 11 (660 KB, el 75 % del
// archivo principal) aunque cada visitante usa uno solo.
const OTROS_IDIOMAS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  it: () => import('./locales/it.json'),
  pt: () => import('./locales/pt.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  pl: () => import('./locales/pl.json'),
  ro: () => import('./locales/ro.json'),
  tl: () => import('./locales/tl.json'),
  sw: () => import('./locales/sw.json'),
};

const backendPerezoso = {
  type: 'backend' as const,
  init() { /* nada */ },
  read(lng: string, _ns: string, cb: (err: unknown, data?: Record<string, unknown>) => void) {
    if (lng === 'es') { cb(null, es as Record<string, unknown>); return; }
    const cargar = OTROS_IDIOMAS[lng];
    if (!cargar) { cb(null, {}); return; }
    cargar().then(m => cb(null, m.default)).catch(err => cb(err));
  },
};

// Idiomas soportados. Añadir nuevos:
//  1. Crear ./locales/XX.json copiando es.json y traduciendo.
//  2. Import arriba y meterlo en `resources`.
//  3. Añadir a SUPPORTED_LANGUAGES.
export const SUPPORTED_LANGUAGES = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português (PT)', flag: '🇵🇹' },
  { code: 'pt-BR', label: 'Português (BR)', flag: '🇧🇷' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'tl', label: 'Filipino', flag: '🇵🇭' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪' },
];

// Custom detector: lee el primer segmento de la URL (/en, /it, /pt, /ro)
// como fuente prioritaria de idioma. Si no hay prefijo de idioma en la
// URL, devuelve 'es' explícitamente (es la raíz del sitio).
// Esto se alinea con el HTML estático generado por build-i18n-html.js,
// donde cada idioma sirve desde su propia carpeta. Importante: NO
// devolver undefined cuando el path es '/', porque entonces el detector
// caería a localStorage y mostraría el idioma anterior cacheado aunque
// la URL ya sea la raíz española.
const pathLanguageDetector = {
  name: 'path',
  lookup: () => {
    if (typeof window === 'undefined') return undefined;
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    if (seg && SUPPORTED_LANGUAGES.some(l => l.code === seg && l.code !== 'es')) {
      return seg;
    }
    // URL sin prefijo de idioma → es (raíz española, autoritativa)
    return 'es';
  },
  cacheUserLanguage: () => {
    // no-op, el caching real lo hace localStorage
  },
};

const languageDetector = new LanguageDetector();
// @ts-expect-error: API interna de i18next-browser-languagedetector
languageDetector.addDetector(pathLanguageDetector);

// Promesa que se cumple cuando el idioma de la página ya está cargado.
// main.tsx espera a ella antes de pintar, para no enseñar un instante el
// español en /en/, /fr/…
export const i18nListo = i18n
  .use(languageDetector)
  .use(backendPerezoso)
  .use(initReactI18next)
  .init({
    resources: { es: { translation: es } },
    partialBundledLanguages: true,
    // Sin Suspense: si alguien cambia de idioma, se sigue viendo el actual
    // hasta que llega el nuevo.
    react: { useSuspense: false },
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),
    interpolation: { escapeValue: false },
    detection: {
      // path (URL /en /it /pt) → querystring (?lang=) → localStorage → navegador
      order: ['path', 'querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'funcomunion_lang',
      caches: ['localStorage'],
    },
  });

export default i18n;
